// The menu as data (contract.ts), made from what the sidebar, the rail's place
// and the phone's Dock already hold: Me's nav, the host's place (cloud: a
// workspace's sections and spaces) and the account's and place's menus.
//
// Promise:
// - Entries come in the sidebar's order: Me's, a separator, the place's, then
//   the place's lists each under its heading (a list with no links is left out).
// - Ids are the Dock's keys (`me:<path>`, `place:<id>`), `list:<id>` for a
//   list's link, `account:<id>` and `place-menu:<id>` for the menus' items, so
//   an outside keeps them across sessions.
// - Entries that go somewhere carry `to`; selecting any id runs what the web
//   runs for it (`run`): going to `to`, or the menu item's own action.

import { iconNameOf } from "../../ui/icon";
import type { MenuItem } from "../../ui/menu";
import {
  SHELL_CONTRACT_VERSION,
  type ShellBadge,
  type ShellMenu,
  type ShellMenuEntry,
  type ShellMenuItem,
  type ShellPlaceSidebar,
  type ShellRail,
} from "./contract";

export interface MenuSourceEntry {
  /** Unique within its group (Me's: its path; the place's: its id). */
  id: string;
  label: string;
  /** An icon component; named when made by `iconOf`. */
  icon?: unknown;
  to: string;
  paths?: readonly string[];
  badge?: ShellBadge | null;
}

/** A place's own sidebar as the web holds it (`ShellPlaceSidebar` with the head's menu as items). */
export interface SidebarSource
  extends Omit<ShellPlaceSidebar, "ongoing" | "menu" | "mine"> {
  mine: readonly string[];
  menu?: readonly (MenuItem | "separator")[];
}

export interface MenuSource {
  account?: {
    name: string;
    image?: string | null;
    menu: readonly (MenuItem | "separator")[];
  };
  /** Me's nav, in its order. */
  me: readonly MenuSourceEntry[];
  place?: {
    id: string;
    name: string;
    items: readonly MenuSourceEntry[];
    lists?: readonly {
      heading: string;
      links: readonly { id: string; label: string; to: string }[];
    }[];
    menu: readonly (MenuItem | "separator")[];
    /** Its own sidebar beside its pages (`ShellPlace.sidebar`, without `ongoing`). */
    sidebar?: SidebarSource | null;
  } | null;
  /** The rail of places, as the host gives it (ids `rail:<place>`). */
  rail?: ShellRail | null;
}

export interface BuiltMenu {
  menu: ShellMenu;
  /** What selecting an id does: a path to go to, or the menu item's action. */
  run: ReadonlyMap<string, string | (() => void)>;
}

export function buildShellMenu(src: MenuSource): BuiltMenu {
  const run = new Map<string, string | (() => void)>();
  const go = (id: string, e: Omit<MenuSourceEntry, "id">): ShellMenuItem => {
    run.set(id, e.to);
    const item: ShellMenuItem = { type: "item", id, label: e.label, to: e.to };
    const icon = iconNameOf(e.icon);
    if (icon) item.icon = icon;
    if (e.paths && e.paths.length > 0) item.paths = [...e.paths];
    if (e.badge) item.badge = e.badge;
    return item;
  };
  const menuOf = (
    prefix: string,
    items: readonly (MenuItem | "separator")[],
  ): ShellMenuEntry[] =>
    items.map((i) => {
      if (i === "separator") return { type: "separator" };
      const id = `${prefix}:${i.id}`;
      run.set(id, i.onSelect);
      const item: ShellMenuItem = { type: "item", id, label: i.label };
      if (i.icon) item.icon = i.icon;
      if (i.danger) item.danger = true;
      if (i.disabled) item.disabled = true;
      return item;
    });

  const entries: ShellMenuEntry[] = src.me.map((e) => go(`me:${e.id}`, e));
  const place = src.place ?? null;
  if (place && place.items.length > 0) {
    if (entries.length > 0) entries.push({ type: "separator" });
    for (const e of place.items) entries.push(go(`place:${e.id}`, e));
  }
  for (const l of place?.lists ?? []) {
    if (l.links.length === 0) continue;
    entries.push({ type: "heading", label: l.heading });
    for (const link of l.links) entries.push(go(`list:${link.id}`, link));
  }

  const menu: ShellMenu = { version: SHELL_CONTRACT_VERSION, entries };
  if (src.account)
    menu.account = {
      name: src.account.name,
      ...(src.account.image ? { image: src.account.image } : {}),
      menu: menuOf("account", src.account.menu),
    };
  if (src.rail) {
    menu.rail = src.rail;
    for (const e of src.rail.entries) run.set(e.id, e.to);
  }
  if (place)
    menu.place = {
      id: place.id,
      name: place.name,
      menu: menuOf("place-menu", place.menu),
      ...(place.sidebar
        ? {
            sidebar: {
              ...place.sidebar,
              mine: [...place.sidebar.mine],
              menu: menuOf("sidebar-menu", place.sidebar.menu ?? []),
            },
          }
        : {}),
    };
  return { menu, run };
}
